import React, { useState, useEffect, useCallback, useMemo } from "react";
import HeaderLayout from "../common/HeaderLayout";
import { useAuth } from "./Authcontext";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "https://api.rangpursadhima.cloud/api";
const PER_PAGE = 50;

const CATEGORIES = [
  { value: "MEDICAL", label: "Medical (તબીબી)" },
  { value: "EDUCATION", label: "Education (શિક્ષણ)" },
  { value: "PROPERTY", label: "Property (મિલકત)" },
  { value: "WORK", label: "Work (કાર્ય)" },
  { value: "ENGAGEMENT", label: "Engagement (સગાઈ)" },
  { value: "VEHICLE", label: "Vehicle (વાહન)" },
  { value: "PAGALA_PADAVAVA", label: "પગલાં પડાવવા" },
  { value: "GENERAL", label: "General (સામાન્ય)" },
];

const ANSWER_STATUSES = [
  { value: "PENDING", label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  { value: "YES", label: "Yes (હા)", color: "bg-green-100 text-green-800" },
  { value: "NO", label: "No (ના)", color: "bg-red-100 text-red-800" },
  { value: "ANSWERED", label: "Answered (જવાબ)", color: "bg-blue-100 text-blue-800" },
];

function calcOverallStatus(answers) {
  if (!answers || answers.length === 0) return "PENDING";
  const hasAnswered = answers.some((a) => a.status === "YES" || a.status === "NO" || a.status === "ANSWERED");
  return hasAnswered ? "ANSWERED" : "PENDING";
}

// ===============================
// PAGINATION
// ===============================
function Pagination({ currentPage, totalPages, totalItems, onPageChange }) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    if (start > 1) { pages.push(1); if (start > 2) pages.push("..."); }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) { if (end < totalPages - 1) pages.push("..."); pages.push(totalPages); }
    return pages;
  };

  const startItem = (currentPage - 1) * PER_PAGE + 1;
  const endItem = Math.min(currentPage * PER_PAGE, totalItems);

  return (
    <div className="bg-white shadow rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-gray-600">
        Showing <span className="font-semibold">{startItem}</span>–<span className="font-semibold">{endItem}</span> of <span className="font-semibold">{totalItems}</span>
      </p>
      <div className="flex items-center gap-1 flex-wrap">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition">← Prev</button>
        {getPageNumbers().map((page, idx) =>
          page === "..." ? (
            <span key={`dots-${idx}`} className="px-2 py-1 text-gray-400 text-sm">…</span>
          ) : (
            <button key={page} onClick={() => onPageChange(page)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${currentPage === page ? "bg-red-600 text-white border-red-600" : "border-gray-300 text-gray-700 hover:bg-gray-100"}`}>{page}</button>
          )
        )}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
          className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition">Next →</button>
      </div>
    </div>
  );
}

// ===============================
// IMAGE LIGHTBOX
// ===============================
function ImageLightbox({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white bg-red-600 hover:bg-red-700 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold z-50">✕</button>
      <img src={src} alt="Full view" onClick={(e) => e.stopPropagation()} className="max-w-full max-h-[90vh] object-contain rounded-lg" />
    </div>
  );
}

function InputField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || label}
        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500" />
    </div>
  );
}

function OptionalDetailsField({ value, onChange }) {
  return (
    <div className="border-t pt-4 mt-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Add Details (વિગતો ઉમેરો) — Optional</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="Enter any additional details here... (વધારાની વિગતો અહીં લખો...)" rows={3}
        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500" />
    </div>
  );
}

// ===============================
// MAIN
// ===============================
export default function QuestionPage() {
  const [activeTab, setActiveTab] = useState("add");
  return (
    <HeaderLayout title="પ્રશ્ન (Question)">
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-4">
          <button onClick={() => setActiveTab("add")}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition ${activeTab === "add" ? "border-red-600 text-red-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
            ➕ Add Question (પ્રશ્ન ઉમેરો)
          </button>
          <button onClick={() => setActiveTab("list")}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition ${activeTab === "list" ? "border-red-600 text-red-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
            📋 Question List (પ્રશ્ન યાદી)
          </button>
        </nav>
      </div>
      {activeTab === "add" ? <AddQuestionTab /> : <QuestionListTab />}
    </HeaderLayout>
  );
}

// ===============================
// ADD QUESTION TAB
// ===============================
function AddQuestionTab() {
  const { user, role, getAuthHeaders } = useAuth();

  const [sevakcode, setSevakcode] = useState("");
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState(null);
  const [sevakLoading, setSevakLoading] = useState(false);
  const [sevakError, setSevakError] = useState("");
  const [category, setCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");

  // Medical
  const [medType, setMedType] = useState("");
  const [medProcess, setMedProcess] = useState("");
  const [doctors, setDoctors] = useState([""]);
  // Education
  const [eduType, setEduType] = useState("");
  const [eduLevel, setEduLevel] = useState("");
  const [institutions, setInstitutions] = useState([""]);
  // Property
  const [propAction, setPropAction] = useState("");
  const [propCondition, setPropCondition] = useState("");
  const [propType, setPropType] = useState("");
  const [sellLandNo, setSellLandNo] = useState("");
  const [sellLandArea, setSellLandArea] = useState("");
  const [sellHouseNo, setSellHouseNo] = useState("");
  const [sellSocietyName, setSellSocietyName] = useState("");
  const [sellHouseArea, setSellHouseArea] = useState("");
  const [buyLandOwner, setBuyLandOwner] = useState("");
  const [buyLandNo, setBuyLandNo] = useState("");
  const [buyLandArea, setBuyLandArea] = useState("");
  const [buyHouseOwner, setBuyHouseOwner] = useState("");
  const [buyHouseNo, setBuyHouseNo] = useState("");
  const [buyHouseSchemeName, setBuyHouseSchemeName] = useState("");
  const [buyHouseArea, setBuyHouseArea] = useState("");
  // Work
  const [workType, setWorkType] = useState("");
  const [workDetails, setWorkDetails] = useState([""]);
  // General
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  // ===== NEW: Engagement =====
  const [engGender, setEngGender] = useState(""); // "Boy" or "Girl"
  const [engNames, setEngNames] = useState([""]);

  // ===== NEW: Vehicle =====
  const [vehType, setVehType] = useState(""); // "Two Wheeler" or "Four Wheeler"
  const [vehCondition, setVehCondition] = useState(""); // "New" or "Old"
  const [vehNewBrands, setVehNewBrands] = useState([""]);
  const [vehOldOwnerName, setVehOldOwnerName] = useState("");
  const [vehOldVehicleNo, setVehOldVehicleNo] = useState("");

  // ===== NEW: Pagala Padavava =====
  const [pagalaDetails, setPagalaDetails] = useState("");

  const lookupSevakcode = useCallback(async () => {
    if (!sevakcode.trim()) return;
    setSevakLoading(true); setSevakError(""); setUserName(""); setUserId(null);
    try {
      const res = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      const found = res.data.find((u) => u.Sevakcode && u.Sevakcode.toLowerCase() === sevakcode.trim().toLowerCase());
      if (found) { setUserName(`${found.FIRST_NAME || ""} ${found.LAST_NAME || ""}`.trim()); setUserId(found.id); }
      else setSevakError("Sevakcode not found");
    } catch (err) { setSevakError("Failed to lookup sevakcode"); }
    finally { setSevakLoading(false); }
  }, [sevakcode, getAuthHeaders]);

  const handlePhotoChange = (e) => { const f = e.target.files[0]; if (f) { setPhoto(f); setPhotoPreview(URL.createObjectURL(f)); } };

  // Medical helpers
  const addDoctor = () => setDoctors([...doctors, ""]);
  const removeDoctor = (i) => setDoctors(doctors.filter((_, idx) => idx !== i));
  const updateDoctor = (i, val) => { const u = [...doctors]; u[i] = val; setDoctors(u); };

  // Education helpers
  const addInstitution = () => setInstitutions([...institutions, ""]);
  const removeInstitution = (i) => setInstitutions(institutions.filter((_, idx) => idx !== i));
  const updateInstitution = (i, val) => { const u = [...institutions]; u[i] = val; setInstitutions(u); };

  // Work helpers
  const addWorkDetail = () => setWorkDetails([...workDetails, ""]);
  const removeWorkDetail = (i) => setWorkDetails(workDetails.filter((_, idx) => idx !== i));
  const updateWorkDetail = (i, val) => { const u = [...workDetails]; u[i] = val; setWorkDetails(u); };

  // Engagement helpers
  const addEngName = () => setEngNames([...engNames, ""]);
  const removeEngName = (i) => setEngNames(engNames.filter((_, idx) => idx !== i));
  const updateEngName = (i, val) => { const u = [...engNames]; u[i] = val; setEngNames(u); };

  // Vehicle helpers
  const addVehBrand = () => setVehNewBrands([...vehNewBrands, ""]);
  const removeVehBrand = (i) => setVehNewBrands(vehNewBrands.filter((_, idx) => idx !== i));
  const updateVehBrand = (i, val) => { const u = [...vehNewBrands]; u[i] = val; setVehNewBrands(u); };

  const resetForm = () => {
    setSevakcode(""); setUserName(""); setUserId(null); setCategory(""); setAdditionalDetails("");
    setMedType(""); setMedProcess(""); setDoctors([""]);
    setEduType(""); setEduLevel(""); setInstitutions([""]);
    setPropAction(""); setPropCondition(""); setPropType("");
    setSellLandNo(""); setSellLandArea(""); setSellHouseNo(""); setSellSocietyName(""); setSellHouseArea("");
    setBuyLandOwner(""); setBuyLandNo(""); setBuyLandArea("");
    setBuyHouseOwner(""); setBuyHouseNo(""); setBuyHouseSchemeName(""); setBuyHouseArea("");
    setWorkType(""); setWorkDetails([""]);
    setPhoto(null); setPhotoPreview("");
    // New resets
    setEngGender(""); setEngNames([""]);
    setVehType(""); setVehCondition(""); setVehNewBrands([""]); setVehOldOwnerName(""); setVehOldVehicleNo("");
    setPagalaDetails("");
  };

  const resetCategoryFields = () => {
    setAdditionalDetails("");
    setMedType(""); setMedProcess(""); setDoctors([""]);
    setEduType(""); setEduLevel(""); setInstitutions([""]);
    setPropAction(""); setPropCondition(""); setPropType("");
    setSellLandNo(""); setSellLandArea(""); setSellHouseNo(""); setSellSocietyName(""); setSellHouseArea("");
    setBuyLandOwner(""); setBuyLandNo(""); setBuyLandArea("");
    setBuyHouseOwner(""); setBuyHouseNo(""); setBuyHouseSchemeName(""); setBuyHouseArea("");
    setWorkType(""); setWorkDetails([""]);
    setPhoto(null); setPhotoPreview("");
    setEngGender(""); setEngNames([""]);
    setVehType(""); setVehCondition(""); setVehNewBrands([""]); setVehOldOwnerName(""); setVehOldVehicleNo("");
    setPagalaDetails("");
  };

  const buildCategoryDetails = () => {
    let details = {};
    switch (category) {
      case "MEDICAL": details = { type: medType, process: medProcess, doctors: doctors.filter((d) => d.trim()) }; break;
      case "EDUCATION": details = { type: eduType, level: eduLevel, institutions: institutions.filter((i) => i.trim()) }; break;
      case "PROPERTY":
        if (propAction === "Sell") {
          if (propType === "Land") details = { action: "Sell", condition: propCondition, propertyType: "Land", landNo: sellLandNo, area: sellLandArea };
          else details = { action: "Sell", condition: propCondition, propertyType: "House", houseNo: sellHouseNo, societyName: sellSocietyName, area: sellHouseArea };
        } else if (propAction === "Buy") {
          if (propType === "Land") details = { action: "Buy", condition: propCondition, propertyType: "Land", ownerName: buyLandOwner, landNo: buyLandNo, area: buyLandArea };
          else details = { action: "Buy", condition: propCondition, propertyType: "House", ownerName: buyHouseOwner, houseNo: buyHouseNo, schemeName: buyHouseSchemeName, area: buyHouseArea };
        }
        break;
      case "WORK": details = { type: workType, details: workDetails.filter((d) => d.trim()) }; break;
      case "ENGAGEMENT":
        details = { gender: engGender, names: engNames.filter((n) => n.trim()) };
        break;
      case "VEHICLE":
        if (vehCondition === "New") {
          details = { vehicleType: vehType, condition: "New", brands: vehNewBrands.filter((b) => b.trim()) };
        } else if (vehCondition === "Old") {
          details = { vehicleType: vehType, condition: "Old", ownerName: vehOldOwnerName, vehicleNumber: vehOldVehicleNo };
        } else {
          details = { vehicleType: vehType };
        }
        break;
      case "PAGALA_PADAVAVA":
        details = { description: pagalaDetails };
        break;
      case "GENERAL": details = { description: "General question with photo" }; break;
      default: break;
    }
    if (additionalDetails.trim()) details.additionalDetails = additionalDetails.trim();
    return details;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sevakcode.trim() || !category) { alert("Sevakcode and Category are required"); return; }
    setSubmitting(true); setSuccessMsg("");
    try {
      const formData = new FormData();
      formData.append("sevakcode", sevakcode.trim());
      formData.append("user_id", userId || "");
      formData.append("user_name", userName || "");
      formData.append("category", category);
      formData.append("category_details", JSON.stringify(buildCategoryDetails()));
      formData.append("actorId", user?.id || 0);
      formData.append("actorRole", role || "ADMIN");
      formData.append("actorEmail", user?.email || "");
      if (photo) formData.append("photo", photo);
      await axios.post(`${API}/questions`, formData, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
      setSuccessMsg("✅ Question added successfully! (પ્રશ્ન સફળતાપૂર્વક ઉમેરાયો!)");
      resetForm();
    } catch (err) { alert("Error: " + (err.response?.data?.error || err.message)); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-6">
      {successMsg && <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded-lg">{successMsg}</div>}

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Sevak Code (સેવક કોડ)</h3>
        <div className="flex items-center gap-3">
          <input type="text" value={sevakcode} onChange={(e) => setSevakcode(e.target.value)} placeholder="Enter Sevak Code (e.g. S-1)"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500" />
          <button type="button" onClick={lookupSevakcode} disabled={sevakLoading}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50">{sevakLoading ? "..." : "Search"}</button>
        </div>
        {userName && <p className="mt-2 text-green-700 font-medium">✅ {userName}</p>}
        {sevakError && <p className="mt-2 text-red-600">{sevakError}</p>}
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Category (વિભાગ)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CATEGORIES.map((cat) => (
            <button key={cat.value} type="button" onClick={() => { setCategory(cat.value); resetCategoryFields(); }}
              className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition ${category === cat.value ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"}`}>{cat.label}</button>
          ))}
        </div>
      </div>

      {/* ===== MEDICAL ===== */}
      {category === "MEDICAL" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Medical Details (તબીબી વિગતો)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type (પ્રકાર)</label>
            <div className="flex gap-3">
              {["Serious", "Regular"].map((t) => (<button key={t} type="button" onClick={() => setMedType(t)} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${medType === t ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{t}</button>))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Process (પ્રક્રિયા)</label>
            <div className="flex gap-3">
              {["Operation", "Checkup"].map((p) => (<button key={p} type="button" onClick={() => setMedProcess(p)} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${medProcess === p ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{p}</button>))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Doctor Name(s) (ડૉક્ટરનું નામ)</label>
            {doctors.map((doc, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input type="text" value={doc} onChange={(e) => updateDoctor(i, e.target.value)} placeholder={`Doctor ${i + 1} name`}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500" />
                {doctors.length > 1 && <button type="button" onClick={() => removeDoctor(i)} className="text-red-500 hover:text-red-700 text-xl font-bold px-2">✕</button>}
              </div>
            ))}
            <button type="button" onClick={addDoctor} className="text-red-600 hover:text-red-700 text-sm font-medium mt-1">+ Add Doctor (ડૉક્ટર ઉમેરો)</button>
          </div>
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {/* ===== EDUCATION ===== */}
      {category === "EDUCATION" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Education Details (શિક્ષણ વિગતો)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type (પ્રકાર)</label>
            <div className="flex gap-3">
              {["Local", "Abroad"].map((t) => (<button key={t} type="button" onClick={() => setEduType(t)} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${eduType === t ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{t}</button>))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Level (સ્તર)</label>
            <div className="flex gap-3">
              {["School", "College", "University"].map((l) => (<button key={l} type="button" onClick={() => setEduLevel(l)} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${eduLevel === l ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{l}</button>))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Institution Name(s) (સંસ્થાનું નામ)</label>
            {institutions.map((inst, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input type="text" value={inst} onChange={(e) => updateInstitution(i, e.target.value)} placeholder={`Institution ${i + 1} name`}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500" />
                {institutions.length > 1 && <button type="button" onClick={() => removeInstitution(i)} className="text-red-500 hover:text-red-700 text-xl font-bold px-2">✕</button>}
              </div>
            ))}
            <button type="button" onClick={addInstitution} className="text-red-600 hover:text-red-700 text-sm font-medium mt-1">+ Add Institution (સંસ્થા ઉમેરો)</button>
          </div>
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {/* ===== PROPERTY ===== */}
      {category === "PROPERTY" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Property Details (મિલકત વિગતો)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Action (ક્રિયા)</label>
            <div className="flex gap-3">
              {["Buy", "Sell"].map((a) => (<button key={a} type="button" onClick={() => { setPropAction(a); setPropCondition(""); setPropType(""); }}
                className={`px-6 py-2 rounded-lg border-2 text-sm font-medium transition ${propAction === a ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{a === "Buy" ? "Buy (ખરીદવું)" : "Sell (વેચવું)"}</button>))}
            </div>
          </div>
          {propAction && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Condition (સ્થિતિ)</label>
              <div className="flex gap-3">
                {["Old", "New"].map((c) => (<button key={c} type="button" onClick={() => { setPropCondition(c); setPropType(""); }}
                  className={`px-6 py-2 rounded-lg border-2 text-sm font-medium transition ${propCondition === c ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{c === "Old" ? "Old (જૂનું)" : "New (નવું)"}</button>))}
              </div>
            </div>
          )}
          {propAction && propCondition && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Property Type (મિલકતનો પ્રકાર)</label>
              <div className="flex gap-3">
                {["Land", "House"].map((t) => (<button key={t} type="button" onClick={() => setPropType(t)}
                  className={`px-6 py-2 rounded-lg border-2 text-sm font-medium transition ${propType === t ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{t === "Land" ? "Land (જમીન)" : "House (મકાન)"}</button>))}
              </div>
            </div>
          )}
          {propAction === "Sell" && propType === "Land" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
              <InputField label="Land No (જમીન નંબર)" value={sellLandNo} onChange={setSellLandNo} />
              <InputField label="Area (વિસ્તાર)" value={sellLandArea} onChange={setSellLandArea} />
            </div>
          )}
          {propAction === "Sell" && propType === "House" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
              <InputField label="House No (મકાન નંબર)" value={sellHouseNo} onChange={setSellHouseNo} />
              <InputField label="Society Name (સોસાયટીનું નામ)" value={sellSocietyName} onChange={setSellSocietyName} />
              <InputField label="Area (વિસ્તાર)" value={sellHouseArea} onChange={setSellHouseArea} />
            </div>
          )}
          {propAction === "Buy" && propType === "Land" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
              <InputField label="Owner Name (માલિકનું નામ)" value={buyLandOwner} onChange={setBuyLandOwner} />
              <InputField label="Land No (જમીન નંબર)" value={buyLandNo} onChange={setBuyLandNo} />
              <InputField label="Area (વિસ્તાર)" value={buyLandArea} onChange={setBuyLandArea} />
            </div>
          )}
          {propAction === "Buy" && propType === "House" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
              <InputField label="Owner Name (માલિકનું નામ)" value={buyHouseOwner} onChange={setBuyHouseOwner} />
              <InputField label="House No (મકાન નંબર)" value={buyHouseNo} onChange={setBuyHouseNo} />
              <InputField label="Scheme Name (યોજનાનું નામ)" value={buyHouseSchemeName} onChange={setBuyHouseSchemeName} />
              <InputField label="Area (વિસ્તાર)" value={buyHouseArea} onChange={setBuyHouseArea} />
            </div>
          )}
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {/* ===== WORK ===== */}
      {category === "WORK" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Work Details (કાર્ય વિગતો)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type (પ્રકાર)</label>
            <div className="flex gap-3">
              {["Business", "Job", "Trading"].map((t) => (<button key={t} type="button" onClick={() => setWorkType(t)} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition ${workType === t ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>{t}</button>))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Details (વિગતો)</label>
            {workDetails.map((detail, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input type="text" value={detail} onChange={(e) => updateWorkDetail(i, e.target.value)} placeholder={`Detail ${i + 1}`}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500" />
                {workDetails.length > 1 && <button type="button" onClick={() => removeWorkDetail(i)} className="text-red-500 hover:text-red-700 text-xl font-bold px-2">✕</button>}
              </div>
            ))}
            <button type="button" onClick={addWorkDetail} className="text-red-600 hover:text-red-700 text-sm font-medium mt-1">+ Add Detail (વિગત ઉમેરો)</button>
          </div>
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {/* ===== ENGAGEMENT (સગાઈ) ===== */}
      {category === "ENGAGEMENT" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Engagement Details (સગાઈ વિગતો)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">For (માટે)</label>
            <div className="flex gap-3">
              {["Boy", "Girl"].map((g) => (
                <button key={g} type="button" onClick={() => { setEngGender(g); setEngNames([""]); }}
                  className={`px-6 py-2 rounded-lg border-2 text-sm font-medium transition ${engGender === g ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>
                  {g === "Boy" ? "Boy (છોકરો)" : "Girl (છોકરી)"}
                </button>
              ))}
            </div>
          </div>
          {engGender && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {engGender === "Boy" ? "Girl Name(s) (છોકરીનું નામ)" : "Boy Name(s) (છોકરાનું નામ)"}
              </label>
              {engNames.map((name, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input type="text" value={name} onChange={(e) => updateEngName(i, e.target.value)}
                    placeholder={`${engGender === "Boy" ? "Girl" : "Boy"} name ${i + 1}`}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500" />
                  {engNames.length > 1 && <button type="button" onClick={() => removeEngName(i)} className="text-red-500 hover:text-red-700 text-xl font-bold px-2">✕</button>}
                </div>
              ))}
              <button type="button" onClick={addEngName} className="text-red-600 hover:text-red-700 text-sm font-medium mt-1">
                + Add Name (નામ ઉમેરો)
              </button>
            </div>
          )}
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {/* ===== VEHICLE (વાહન) ===== */}
      {category === "VEHICLE" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Vehicle Details (વાહન વિગતો)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type (વાહનનો પ્રકાર)</label>
            <div className="flex gap-3">
              {["Two Wheeler", "Four Wheeler"].map((t) => (
                <button key={t} type="button" onClick={() => { setVehType(t); setVehCondition(""); setVehNewBrands([""]); setVehOldOwnerName(""); setVehOldVehicleNo(""); }}
                  className={`px-6 py-2 rounded-lg border-2 text-sm font-medium transition ${vehType === t ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>
                  {t === "Two Wheeler" ? "Two Wheeler (ટુ વ્હીલર)" : "Four Wheeler (ફોર વ્હીલર)"}
                </button>
              ))}
            </div>
          </div>
          {vehType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Condition (સ્થિતિ)</label>
              <div className="flex gap-3">
                {["New", "Old"].map((c) => (
                  <button key={c} type="button" onClick={() => { setVehCondition(c); setVehNewBrands([""]); setVehOldOwnerName(""); setVehOldVehicleNo(""); }}
                    className={`px-6 py-2 rounded-lg border-2 text-sm font-medium transition ${vehCondition === c ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>
                    {c === "New" ? "New (નવું)" : "Old (જૂનું)"}
                  </button>
                ))}
              </div>
            </div>
          )}
          {vehCondition === "New" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Brand Name(s) (બ્રાન્ડનું નામ)</label>
              {vehNewBrands.map((brand, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input type="text" value={brand} onChange={(e) => updateVehBrand(i, e.target.value)} placeholder={`Brand ${i + 1}`}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-500" />
                  {vehNewBrands.length > 1 && <button type="button" onClick={() => removeVehBrand(i)} className="text-red-500 hover:text-red-700 text-xl font-bold px-2">✕</button>}
                </div>
              ))}
              <button type="button" onClick={addVehBrand} className="text-red-600 hover:text-red-700 text-sm font-medium mt-1">+ Add Brand (બ્રાન્ડ ઉમેરો)</button>
            </div>
          )}
          {vehCondition === "Old" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
              <InputField label="Owner Name (માલિકનું નામ)" value={vehOldOwnerName} onChange={setVehOldOwnerName} />
              <InputField label="Vehicle Number (વાહન નંબર)" value={vehOldVehicleNo} onChange={setVehOldVehicleNo} />
            </div>
          )}
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {/* ===== PAGALA PADAVAVA (પગલાં પડાવવા) ===== */}
      {category === "PAGALA_PADAVAVA" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">પગલાં પડાવવા Details</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Details (વિગતો)</label>
            <textarea value={pagalaDetails} onChange={(e) => setPagalaDetails(e.target.value)}
              placeholder="પગલાં પડાવવા વિશે વિગતો લખો..." rows={4}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500" />
          </div>
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {/* ===== GENERAL ===== */}
      {category === "GENERAL" && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">General (સામાન્ય) - Upload Photo</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Click Photo & Upload (ફોટો અપલોડ કરો)</label>
            <input type="file" accept="image/*" capture="environment" onChange={handlePhotoChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100" />
          </div>
          <GeneralPhotoPreview photoPreview={photoPreview} />
          <OptionalDetailsField value={additionalDetails} onChange={setAdditionalDetails} />
        </div>
      )}

      {category && (
        <div className="flex justify-end">
          <button type="submit" disabled={submitting || !sevakcode.trim()}
            className="bg-red-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-red-700 disabled:opacity-50 transition">
            {submitting ? "Submitting..." : "Submit Question (પ્રશ્ન સબમિટ કરો)"}
          </button>
        </div>
      )}
    </form>
  );
}

function GeneralPhotoPreview({ photoPreview }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  if (!photoPreview) return null;
  return (
    <div className="mt-3">
      <img src={photoPreview} alt="Preview" onClick={() => setLightboxOpen(true)}
        className="max-w-xs max-h-64 rounded-lg border border-gray-300 object-contain cursor-pointer hover:opacity-80 transition" />
      {lightboxOpen && <ImageLightbox src={photoPreview} onClose={() => setLightboxOpen(false)} />}
    </div>
  );
}

// ===============================
// QUESTION LIST TAB (with pagination)
// ===============================
function QuestionListTab() {
  const { role, user, getAuthHeaders } = useAuth();
  const isSuperAdmin = role === "SUPERADMIN";

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [sevakSearch, setSevakSearch] = useState("");
  const [sevakProfile, setSevakProfile] = useState(null);
  const [sevakSearching, setSevakSearching] = useState(false);
  const [sevakCounts, setSevakCounts] = useState({});
  const [sevakPage, setSevakPage] = useState(1);

  const fetchAllCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/questions`, { headers: getAuthHeaders() });
      const counts = {};
      res.data.forEach((q) => { const code = (q.sevakcode || "").toUpperCase(); if (code) counts[code] = (counts[code] || 0) + 1; });
      setSevakCounts(counts);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append("category", filterCategory);
      if (filterStatus) params.append("status", filterStatus);
      const res = await axios.get(`${API}/questions?${params.toString()}`, { headers: getAuthHeaders() });
      setQuestions(res.data);
      setCurrentPage(1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterCategory, filterStatus, getAuthHeaders]);

  useEffect(() => { fetchAllCounts(); }, [fetchAllCounts]);
  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const openSevakProfile = useCallback(async (codeToSearch) => {
    const code = (codeToSearch || sevakSearch || "").trim();
    if (!code) { setSevakProfile(null); return; }
    setSevakSearching(true); setSevakSearch(code);
    try {
      const usersRes = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      const found = usersRes.data.find((u) => u.Sevakcode && u.Sevakcode.toLowerCase() === code.toLowerCase());
      if (!found) { setSevakProfile({ error: "Sevakcode not found" }); setSevakSearching(false); return; }

      const qRes = await axios.get(`${API}/questions?sevakcode=${code}`, { headers: getAuthHeaders() });
      const sorted = qRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      const categoryCounts = {};
      CATEGORIES.forEach((c) => { categoryCounts[c.value] = 0; });
      sorted.forEach((q) => { if (categoryCounts[q.category] !== undefined) categoryCounts[q.category]++; });

      const statusCounts = { PENDING: 0, YES: 0, NO: 0, ANSWERED: 0 };
      sorted.forEach((q) => { const st = calcOverallStatus(q.answers); if (statusCounts[st] !== undefined) statusCounts[st]++; });

      setSevakProfile({ user: found, questions: sorted, totalQuestions: sorted.length, categoryCounts, statusCounts, error: null });
      setSevakPage(1);
    } catch (err) { setSevakProfile({ error: "Failed to search" }); }
    finally { setSevakSearching(false); }
  }, [sevakSearch, getAuthHeaders]);

  const clearSevakSearch = () => { setSevakSearch(""); setSevakProfile(null); setSevakPage(1); };

  const updateAnswers = async (questionId, answers) => {
    try {
      await axios.put(`${API}/questions/${questionId}/answer`,
        { answers, actorId: user?.id, actorRole: role, actorEmail: user?.email }, { headers: getAuthHeaders() });
      setQuestions((prev) => prev.map((q) => q.id === questionId ? { ...q, answers } : q));
    } catch (err) { console.error("Failed to update answers:", err); }
  };

  const filtered = useMemo(() => {
    return questions.filter((q) => {
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      return (q.sevakcode || "").toLowerCase().includes(term) || (q.user_name || "").toLowerCase().includes(term) ||
        (q.FIRST_NAME || "").toLowerCase().includes(term) || (q.LAST_NAME || "").toLowerCase().includes(term) ||
        (q.category || "").toLowerCase().includes(term);
    });
  }, [questions, searchTerm]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginatedList = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const sevakQuestions = sevakProfile?.questions || [];
  const sevakTotalPages = Math.ceil(sevakQuestions.length / PER_PAGE);
  const paginatedSevak = sevakQuestions.slice((sevakPage - 1) * PER_PAGE, sevakPage * PER_PAGE);

  const handlePageChange = (page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const handleSevakPageChange = (page) => { setSevakPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="space-y-4">
      {/* Sevak Code Search */}
      <div className="bg-white shadow rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">🔍 Search by Sevak Code (સેવક કોડથી શોધો)</h3>
        <div className="flex items-center gap-3">
          <input type="text" value={sevakSearch} onChange={(e) => setSevakSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") openSevakProfile(); }}
            placeholder="Enter Sevak Code (e.g. S-1)"
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500" />
          <button type="button" onClick={() => openSevakProfile()} disabled={sevakSearching}
            className="bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium">
            {sevakSearching ? "Searching..." : "Search"}
          </button>
          {sevakProfile && (
            <button type="button" onClick={clearSevakSearch}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm font-medium">Clear</button>
          )}
        </div>
      </div>

      {/* SEVAK PROFILE VIEW */}
      {sevakProfile && sevakProfile.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{sevakProfile.error}</div>
      )}

      {sevakProfile && !sevakProfile.error && (
        <>
          <div className="bg-white shadow-lg rounded-lg p-6 border-l-4 border-red-600">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{sevakProfile.user.FIRST_NAME} {sevakProfile.user.LAST_NAME}</h2>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p><span className="font-medium">Sevak Code:</span> <span className="text-red-700 font-semibold">{sevakProfile.user.Sevakcode}</span></p>
                  {sevakProfile.user.MOBILE_NO && <p><span className="font-medium">Mobile:</span> {sevakProfile.user.MOBILE_NO}</p>}
                  {sevakProfile.user.DISTRICT && <p><span className="font-medium">District:</span> {sevakProfile.user.DISTRICT}</p>}
                </div>
              </div>
              <div className="text-center bg-red-50 rounded-xl px-6 py-4 border border-red-200">
                <p className="text-3xl font-bold text-red-700">{sevakProfile.totalQuestions}</p>
                <p className="text-xs text-red-600 font-medium mt-1">Total Questions (કુલ પ્રશ્નો)</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {CATEGORIES.map((cat) => {
                const count = sevakProfile.categoryCounts[cat.value] || 0;
                const cm = {
                  MEDICAL: "bg-blue-50 text-blue-700 border-blue-200",
                  EDUCATION: "bg-purple-50 text-purple-700 border-purple-200",
                  PROPERTY: "bg-orange-50 text-orange-700 border-orange-200",
                  WORK: "bg-teal-50 text-teal-700 border-teal-200",
                  ENGAGEMENT: "bg-pink-50 text-pink-700 border-pink-200",
                  VEHICLE: "bg-indigo-50 text-indigo-700 border-indigo-200",
                  PAGALA_PADAVAVA: "bg-amber-50 text-amber-700 border-amber-200",
                  GENERAL: "bg-gray-50 text-gray-700 border-gray-200",
                };
                return (<div key={cat.value} className={`rounded-lg px-3 py-2 text-center border ${cm[cat.value] || "bg-gray-50 text-gray-700 border-gray-200"}`}><p className="text-lg font-bold">{count}</p><p className="text-xs font-medium">{cat.label.split(" ")[0]}</p></div>);
              })}
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              {ANSWER_STATUSES.map((s) => {
                const count = sevakProfile.statusCounts[s.value] || 0;
                return (<div key={s.value} className={`rounded-lg px-3 py-2 text-center ${s.color}`}><p className="text-lg font-bold">{count}</p><p className="text-xs font-medium">{s.label}</p></div>);
              })}
            </div>
          </div>

          <div className="px-1">
            <h3 className="text-sm font-semibold text-gray-500">All Questions — Latest First ({sevakProfile.totalQuestions} total)</h3>
          </div>

          <Pagination currentPage={sevakPage} totalPages={sevakTotalPages} totalItems={sevakQuestions.length} onPageChange={handleSevakPageChange} />

          {paginatedSevak.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No questions found for this Sevak</div>
          ) : (
            <div className="space-y-4">
              {paginatedSevak.map((q, idx) => {
                const globalIdx = (sevakPage - 1) * PER_PAGE + idx;
                const prevQ = globalIdx > 0 ? sevakQuestions[globalIdx - 1] : null;
                return (
                  <div key={q.id}>
                    {(!prevQ || formatDate(q.created_at) !== formatDate(prevQ.created_at)) && (
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-px bg-gray-300 flex-1"></div>
                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{formatDate(q.created_at)}</span>
                        <div className="h-px bg-gray-300 flex-1"></div>
                      </div>
                    )}
                    <QuestionCard question={q} isSuperAdmin={isSuperAdmin} updateAnswers={updateAnswers}
                      showSevakInfo={false} sevakCounts={sevakCounts} onSevakClick={openSevakProfile} />
                  </div>
                );
              })}
            </div>
          )}

          <Pagination currentPage={sevakPage} totalPages={sevakTotalPages} totalItems={sevakQuestions.length} onPageChange={handleSevakPageChange} />
        </>
      )}

      {/* GENERAL LIST VIEW */}
      {!sevakProfile && (
        <>
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by Sevakcode or Name..."
                className="border border-gray-300 rounded-lg px-4 py-2 text-sm flex-1 min-w-[200px] focus:ring-2 focus:ring-red-500" />
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Categories</option>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Status</option>
                {ANSWER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} onPageChange={handlePageChange} />

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : paginatedList.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No questions found</div>
          ) : (
            <div className="space-y-4">
              {paginatedList.map((q) => (
                <QuestionCard key={q.id} question={q} isSuperAdmin={isSuperAdmin} updateAnswers={updateAnswers}
                  showSevakInfo={true} sevakCounts={sevakCounts} onSevakClick={openSevakProfile} />
              ))}
            </div>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} onPageChange={handlePageChange} />
        </>
      )}
    </div>
  );
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ===============================
// QUESTION CARD
// ===============================
function QuestionCard({ question, isSuperAdmin, updateAnswers, showSevakInfo = true, sevakCounts = {}, onSevakClick }) {
  const q = question;
  const d = q.category_details || {};
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [answers, setAnswers] = useState(q.answers || []);
  const [saveTimeout, setSaveTimeout] = useState(null);

  const overallStatus = calcOverallStatus(answers);
  const overallStatusObj = ANSWER_STATUSES.find((s) => s.value === overallStatus) || ANSWER_STATUSES[0];
  const sevakTotal = sevakCounts[(q.sevakcode || "").toUpperCase()] || 0;

  const setItemStatus = (index, status) => {
    const updated = [...answers]; updated[index] = { ...updated[index], status }; setAnswers(updated);
    if (saveTimeout) clearTimeout(saveTimeout);
    updateAnswers(q.id, updated);
  };

  const setItemText = (index, text) => {
    const updated = [...answers]; updated[index] = { ...updated[index], text }; setAnswers(updated);
    if (saveTimeout) clearTimeout(saveTimeout);
    const timeout = setTimeout(() => { updateAnswers(q.id, updated); }, 800);
    setSaveTimeout(timeout);
  };

  const getCategoryColor = () => {
    switch (q.category) {
      case "MEDICAL": return "bg-blue-100 text-blue-800";
      case "EDUCATION": return "bg-purple-100 text-purple-800";
      case "PROPERTY": return "bg-orange-100 text-orange-800";
      case "WORK": return "bg-teal-100 text-teal-800";
      case "ENGAGEMENT": return "bg-pink-100 text-pink-800";
      case "VEHICLE": return "bg-indigo-100 text-indigo-800";
      case "PAGALA_PADAVAVA": return "bg-amber-100 text-amber-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const renderDetails = () => {
    switch (q.category) {
      case "MEDICAL": return (<div className="text-sm text-gray-600 space-y-1">{d.type && <p><span className="font-medium">Type:</span> {d.type}</p>}{d.process && <p><span className="font-medium">Process:</span> {d.process}</p>}</div>);
      case "EDUCATION": return (<div className="text-sm text-gray-600 space-y-1">{d.type && <p><span className="font-medium">Type:</span> {d.type}</p>}{d.level && <p><span className="font-medium">Level:</span> {d.level}</p>}</div>);
      case "PROPERTY": return (<div className="text-sm text-gray-600 space-y-1">
        {d.action && <p><span className="font-medium">Action:</span> {d.action}</p>}
        {d.condition && <p><span className="font-medium">Condition:</span> {d.condition}</p>}
        {d.propertyType && <p><span className="font-medium">Type:</span> {d.propertyType}</p>}
        {d.ownerName && <p><span className="font-medium">Owner:</span> {d.ownerName}</p>}
        {d.landNo && <p><span className="font-medium">Land No:</span> {d.landNo}</p>}
        {d.houseNo && <p><span className="font-medium">House No:</span> {d.houseNo}</p>}
        {d.societyName && <p><span className="font-medium">Society:</span> {d.societyName}</p>}
        {d.schemeName && <p><span className="font-medium">Scheme:</span> {d.schemeName}</p>}
        {d.area && <p><span className="font-medium">Area:</span> {d.area}</p>}
      </div>);
      case "WORK": return (<div className="text-sm text-gray-600 space-y-1">{d.type && <p><span className="font-medium">Type:</span> {d.type}</p>}</div>);
      case "ENGAGEMENT": return (<div className="text-sm text-gray-600 space-y-1">
        {d.gender && <p><span className="font-medium">For:</span> {d.gender === "Boy" ? "Boy (છોકરો)" : "Girl (છોકરી)"}</p>}
        {d.gender && <p><span className="font-medium">{d.gender === "Boy" ? "Girl Names:" : "Boy Names:"}</span></p>}
      </div>);
      case "VEHICLE": return (<div className="text-sm text-gray-600 space-y-1">
        {d.vehicleType && <p><span className="font-medium">Type:</span> {d.vehicleType}</p>}
        {d.condition && <p><span className="font-medium">Condition:</span> {d.condition}</p>}
        {d.ownerName && <p><span className="font-medium">Owner:</span> {d.ownerName}</p>}
        {d.vehicleNumber && <p><span className="font-medium">Vehicle No:</span> {d.vehicleNumber}</p>}
      </div>);
      case "PAGALA_PADAVAVA": return (<div className="text-sm text-gray-600 space-y-1">
        {d.description && <p>{d.description}</p>}
      </div>);
      case "GENERAL": return q.photo_path ? (
        <><img src={`${API}${q.photo_path}`} alt="Question" onClick={() => setLightboxOpen(true)}
          className="max-w-xs max-h-48 rounded-lg border object-contain mt-2 cursor-pointer hover:opacity-80 transition" />
          {lightboxOpen && <ImageLightbox src={`${API}${q.photo_path}`} onClose={() => setLightboxOpen(false)} />}</>
      ) : (<p className="text-sm text-gray-400">No photo</p>);
      default: return null;
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg font-bold text-gray-900">#{q.id}</span>
          {showSevakInfo && (
            <>
              <span className="font-semibold text-red-700">{q.sevakcode}</span>
              {sevakTotal > 0 && onSevakClick && (
                <button type="button" onClick={() => onSevakClick(q.sevakcode)}
                  className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs font-bold px-2.5 py-1 rounded-full border border-red-200 hover:bg-red-100 hover:border-red-400 transition cursor-pointer"
                  title={`View all ${sevakTotal} questions for ${q.sevakcode}`}>📋 {sevakTotal} Questions</button>
              )}
              <span className="text-gray-600">{q.user_name || `${q.FIRST_NAME || ""} ${q.LAST_NAME || ""}`.trim() || "—"}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getCategoryColor()}`}>{q.category === "PAGALA_PADAVAVA" ? "પગલાં પડાવવા" : q.category}</span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${overallStatusObj.color}`}>{overallStatusObj.label}</span>
        </div>
      </div>

      <div className="mb-3">{renderDetails()}</div>

      {d.additionalDetails && (
        <div className="mb-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
          <p className="text-xs font-semibold text-gray-500 mb-1">Additional Details (વિગતો):</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{d.additionalDetails}</p>
        </div>
      )}

      <p className="text-xs text-gray-400 mb-3">
        {new Date(q.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}
      </p>

      {answers && answers.length > 0 && (
        <div className="border-t pt-3 mt-3 space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            {answers.length === 1 && answers[0].item === "main" ? "Answer (જવાબ):" : "Per-item Answers (દરેક માટે જવાબ):"}
          </p>
          {answers.map((ans, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-3 space-y-2">
              {ans.item !== "main" && <p className="text-sm font-medium text-gray-800">{idx + 1}. {ans.item}</p>}
              {!isSuperAdmin ? (
                <>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${ANSWER_STATUSES.find((s) => s.value === ans.status)?.color || "bg-gray-100 text-gray-800"}`}>
                    {ANSWER_STATUSES.find((s) => s.value === ans.status)?.label || ans.status}
                  </span>
                  {ans.text && <p className="text-sm text-gray-600 bg-white rounded p-2 border"><span className="font-medium">Answer:</span> {ans.text}</p>}
                </>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {ANSWER_STATUSES.map((s) => (
                      <button key={s.value} type="button" onClick={() => setItemStatus(idx, s.value)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium border-2 transition ${ans.status === s.value ? "border-red-600 bg-red-50 text-red-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>{s.label}</button>
                    ))}
                  </div>
                  <textarea value={ans.text || ""} onChange={(e) => setItemText(idx, e.target.value)}
                    placeholder="Write answer... (auto-saves)" rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500" />
                </>
              )}
            </div>
          ))}
          {isSuperAdmin && <p className="text-xs text-gray-400">Auto-saves after typing</p>}
        </div>
      )}
    </div>
  );
}